import { isNativeMobileApp } from "../utils/platform.helpers";

import { currencyService } from "./CurrencyService";

export function formatCurrency(value: number, maximumFractionDigits?: number) {
  // Backend prices are whole minor units. A fractional value is a caller bug, and
  // rendering it would silently show a price the backend never quoted.
  if (!Number.isInteger(value)) {
    throw new TypeError("Amount must be an integer value");
  }

  const currency = currencyService.getCurrency();
  const minorUnits = 10 ** currency.decimalDigits;
  // `value === 0` also catches -0, which JSON.parse produces and Intl renders as "-$0".
  const amount = value === 0 ? 0 : value / minorUnits;

  // Auto-detect if we should hide cents when they are 0
  const hasCents = value % minorUnits !== 0;
  const fractionDigits =
    maximumFractionDigits !== undefined ? maximumFractionDigits : hasCents ? 2 : 0;

  if (isNativeMobileApp()) {
    // Custom formatting for Capacitor because Intl.NumberFormat throws a
    // RangeError when maximumFractionDigits is 0
    const formattedValue = amount
      .toFixed(fractionDigits)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `$${formattedValue}`;
  } else {
    // Use Intl.NumberFormat for web. narrowSymbol keeps CAD on a plain "$" so web
    // matches the native branch above; the currency itself is stated once on the
    // order total (see getCurrencyCode). No-op for USD.
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.code,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(amount);
  }
}

/**
 * ISO 4217 code for the active platform's currency, for labelling a total that
 * `formatCurrency` renders with an ambiguous "$".
 */
export const getCurrencyCode = () => currencyService.getCurrency().code;

/**
 * Formats a backend price value (in cents) to a price string without currency
 * symbol (in dollars).
 */
export const formatNoCurrency = (value: number, maximumFractionDigits = 2) =>
  (value / 100).toFixed(maximumFractionDigits);

/**
 * Parse a formatted price string to a backend price value (in cents). Accepts
 * string both with and without currency symbol.
 */
export const parsePriceString = (value: string) => {
  const parsedValue = Number(value.replace(/[$,]/g, ""));
  return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : null;
};
